/**
 * BLK-35 — a counter that says which session a network result belongs to.
 *
 * The refresh call is a promise nobody can cancel: `firstValueFrom` on an
 * `HttpBackend` client, with no `takeUntil` and no `AbortController`. When the
 * user pressed Logout while one was in flight, the response still landed and
 * still ran `setAccessToken(...)` — so the token came back AFTER logout, and
 * `guestGuard`, which only reads `getAccessToken()`, bounced the user off the
 * login page and back into the app. Worse, the interceptor then re-issued the
 * original request with that fresh bearer: an authenticated call made after the
 * user asked to be signed out.
 *
 * Cancelling is not really available here, so the result is judged instead of
 * prevented. Logout bumps the epoch; a refresh captures it before the request
 * and writes nothing if it changed while the request was in the air.
 *
 * It lives beside the token stores, NOT on AuthService, for one specific reason:
 * `clearAuthSession()` is a free function in `auth-session.util.ts` and cannot
 * reach a private static on the service. That is exactly why the bug existed.
 */
let epoch = 0;

export const authEpochStore = {
  /** The epoch a caller should capture before starting a request. */
  current(): number {
    return epoch;
  },
  /** Invalidate every result captured before now. Called on logout. */
  bump(): void {
    epoch += 1;
  },
};

/**
 * Whether a result captured at `capturedEpoch` may still be applied.
 *
 * Pure so it can be tested without a browser: the wiring around it is trivial,
 * the rule is what has to be right.
 */
export function isSessionResultCurrent(
  capturedEpoch: number,
  currentEpoch: number = authEpochStore.current(),
): boolean {
  return capturedEpoch === currentEpoch;
}
